import { IdxStep, TokenParams } from '@mgmresorts/mgm-auth-js-sdk';
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';

import { OktaUser } from '../../../../@types/okta-user';
import { EmailVerification } from '../../../components/ui/EmailVerification';
import { useCountdown } from '../../../hooks';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { useInput } from '../../../hooks/useInput';
import { useLoadingLabel } from '../../../hooks/useLoadingLabel';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { ClientId } from '../../../utils/constants';
import { getFeaturesByClientId } from '../../../utils/getFeaturesByClientId';
import { getThirdPartyScopesByClientId } from '../../../utils/getScopes';
import { linkAccountToPartner } from '../../../utils/linkAccountToPartner';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { getUrlParams } from '../../../utils/url';
import { UserAccountContext } from '../../../utils/userAccountContext';

// SessionStorage lock to prevent duplicate IDX challenge calls when Adobe DTM
// causes page remount in production (same pattern as enrollment deduplication)
const MFA_EMAIL_CHALLENGE_LOCK_KEY = 'mfa_email_challenge_lock';
const CHALLENGE_LOCK_TTL_MS = 15000; // 15 seconds

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function EmailVerificationSignin({ type }: { type: 'enrollment' | 'authentication' }) {
  const redirectToApp = useAppRedirect();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const eventNameError = getContentByPath<string>('mfa.analytics.error.eventName');
  const trackSuffixError = getContentByPath<string>('mfa.analytics.error.trackSuffix');
  usePageLoadEvent('identity', type === 'enrollment' ? '2fa setup: email auth code' : 'sign in: email auth');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setLoadingState] = useState(false);
  const submitCTA = useLoadingLabel(isLoading, 'EmailVerificationSignin.submit');
  const { authClient } = useAuthContext();
  const {
    bind: bindVerificationCode,
    value: verificationCode,
    reset: resetVerificationCode,
  } = useInput('', 'verificationCode');
  const [inputError, setInputError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [codeResentSuccessfully, setCodeResentSuccessfully] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const emailSentRef = useRef(false);
  const { email } = useContext(UserAccountContext);

  const emailOtpHandler = authClient?.idx?.stepHandlers?.[IdxStep.AuthEmail];
  const emailConfirmationHandler = authClient?.idx?.stepHandlers?.[IdxStep.AuthEmailConfirm];

  const countDown = useCountdown(emailOtpHandler?.resendRateLimitSeconds || 30);

  useEffect(() => {
    if (countDown.startCountdown) {
      countDown.startCountdown();
    }
  }, [countDown]);

  useEffect(() => {
    // Don't run if handlers aren't available
    if (!emailConfirmationHandler) return;

    // Prevent duplicate email sends (React 18 Strict Mode calls useEffect twice)
    if (emailSentRef.current) return;

    // Check sessionStorage lock to prevent duplicate challenge calls when Adobe DTM
    // causes a full page remount in production via iframe creation
    try {
      const lockTs = sessionStorage.getItem(MFA_EMAIL_CHALLENGE_LOCK_KEY);
      if (lockTs && Date.now() - parseInt(lockTs, 10) < CHALLENGE_LOCK_TTL_MS) {
        // Another instance already triggered the challenge — skip to avoid duplicate
        emailSentRef.current = true;
        setOpenAlert(true);
        setCodeResentSuccessfully(true);
        return;
      }
      // Set optimistic lock before making the call
      sessionStorage.setItem(MFA_EMAIL_CHALLENGE_LOCK_KEY, String(Date.now()));
    } catch (e) {
      // sessionStorage unavailable — fall through to normal behavior
    }

    emailSentRef.current = true;

    const sendCodeToEmail = async () => {
      try {
        await emailConfirmationHandler.confirmEmailAuth();
        setOpenAlert(true);
        setCodeResentSuccessfully(true);

        const message = getContentByPath('mfa.email.codeResentSuccess') as string;
        await trackEvent(
          'identity – unified MFA notification',
          message,
          'unifiedmfanotification',
          { notificationDisplayed: message },
          true,
        );
      } catch (err) {
        console.error('Error during email authentication setup:', err);
        setErrorMessage(getContentByPath('mfa.email.codeResentFail'));
        await trackEvent(eventNameError, getContentByPath('mfa.email.codeResentFail'), trackSuffixError, {}, true);
        setOpenAlert(true);
      }
    };

    sendCodeToEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateCode = useCallback(() => {
    setTouched(true);
    if (verificationCode.length === 0) {
      setInputError(getContentByPath('mfa.email.fields.verificationCode.required'));
      trackEvent(
        eventNameError,
        getContentByPath('mfa.email.fields.verificationCode.required'),
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    if (verificationCode.length > 0 && verificationCode.length < 6) {
      setInputError(getContentByPath('mfa.email.fields.verificationCode.invalid'));
      trackEvent(
        eventNameError,
        getContentByPath('mfa.email.fields.verificationCode.invalid'),
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    setInputError('');
    return true;
  }, [verificationCode, getContentByPath, trackEvent, eventNameError, trackSuffixError]);

  const handleVerificationCodeBlur = () => {
    validateCode();
  };

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Filter to allow only numeric input
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: numericValue },
    };

    bindVerificationCode.onChange(syntheticEvent);

    // Clear error if user is typing and field becomes valid
    if (touched) {
      if (numericValue.length === 6) {
        setInputError('');
      } else if (numericValue.length > 0 && numericValue.length < 6) {
        setInputError(getContentByPath('mfa.email.fields.verificationCode.invalid') || 'Invalid code');
        trackEvent(
          eventNameError,
          getContentByPath('mfa.email.fields.verificationCode.invalid') || 'Invalid code',
          trackSuffixError,
          {},
          true,
        );
      }
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!validateCode()) {
      return;
    }
    if (!emailOtpHandler) {
      console.error('Email OTP handler not available');
      return;
    }

    await trackEvent('identity – unified MFA CTAs', 'Email Authentication Code - Submit', 'unifiedmfactas', {}, true);

    setLoadingState(true);
    try {
      await emailOtpHandler.emailAuth(verificationCode);
      const tokens = authClient.idx.idxTransaction?.tokens;
      if (tokens) {
        authClient.getAuthClient().tokenManager.setTokens(tokens);
      } else {
        console.warn('No tokens found in IDX transaction after email auth');
        throw new Error('No tokens available after MFA verification');
      }

      // Get persisted parameters to check client configuration
      const params = getPersistedParameters();
      const { client_id } = params;
      const { isAllowOktaSignInWithRedirect } = getFeaturesByClientId(client_id as ClientId);

      if (isAllowOktaSignInWithRedirect) {
        const urlParams = getUrlParams();
        const options: TokenParams = {
          redirectUri: urlParams.redirectUri,
          codeChallenge: urlParams.codeChallenge,
          scopes: getThirdPartyScopesByClientId(urlParams.clientId as ClientId),
          state: urlParams.state,
        };
        await authClient.getAuthClient().signInWithRedirect(options);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      // Verify tokens are actually in the token manager
      const accessToken = await authClient.getAccessToken();
      const idToken = await authClient.getIdToken();

      if (accessToken && idToken) {
        await trackEvent(
          'identity – sign in - credentials submit success',
          'sign in credentials submit success',
          'signinsubmitsuccess',
          { signInState: 'success' },
          true,
        );

        // Handle partner linking if needed
        if (params.partner && params.tokenKey) {
          try {
            const activeSessionObj = await authClient.hasActiveMemberSession();
            if (activeSessionObj?.activeMemberSession && activeSessionObj?.session?.user) {
              const user = (await activeSessionObj.session.user()) as OktaUser;
              await linkAccountToPartner(params.partner as string, params.tokenKey as string, user.profile.mlifeNumber);
            }
          } catch (err) {
            console.warn('Partner linking failed:', err);
          }
        }
        redirectToApp();
      } else {
        console.error('Tokens not found in token manager after setting');
        throw new Error('Failed to persist tokens in token manager');
      }
    } catch (err) {
      setCodeResentSuccessfully(false);
      console.error('Error during email authentication:', err);
      setInputError(getContentByPath('mfa.email.invalidCode'));
      trackEvent(eventNameError, getContentByPath('mfa.email.invalidCode'), trackSuffixError, {}, true);
      setOpenAlert(true);
      setLoadingState(false);
    }
  }, [
    redirectToApp,
    authClient,
    emailOtpHandler,
    verificationCode,
    getContentByPath,
    validateCode,
    trackEvent,
    eventNameError,
    trackSuffixError,
  ]);

  const handleResendCode = async () => {
    if (!emailOtpHandler) return;

    if (!countDown.isRunning && emailOtpHandler.canResendCode) {
      try {
        await trackEvent(
          'identity – unified MFA CTAs',
          'Resend Authentication Code - Email',
          'unifiedmfactas',
          {},
          true,
        );

        await emailOtpHandler.resendCode();
        setOpenAlert(true);
        setCodeResentSuccessfully(true);
        setErrorMessage('');
        setInputError('');
        setTouched(false);
        resetVerificationCode();
        countDown.startCountdown();

        const message = getContentByPath('mfa.email.codeResentSuccess') as string;
        await trackEvent(
          'identity – unified MFA notification',
          message,
          'unifiedmfanotification',
          { notificationDisplayed: message },
          true,
        );
      } catch (err) {
        setCodeResentSuccessfully(false);
        setOpenAlert(true);
        setErrorMessage(getContentByPath('mfa.email.codeResentFail'));
        trackEvent(eventNameError, getContentByPath('mfa.email.codeResentFail'), trackSuffixError, {}, true);
      }
    }
  };

  const maskEmail = (email: string) => {
    return email.replace(/^(.)(.*)(.)@/, (_, first, middle, last) => `${first}...${last}@`);
  };

  const onCloseAlert = () => {
    setOpenAlert(false);
    setErrorMessage('');
  };

  return (
    <div data-testid={type === 'enrollment' ? 'route-/mfa/enroll/email' : 'route-/mfa/auth/email'}>
      <EmailVerification
        onCloseAlert={onCloseAlert}
        email={maskEmail(email)}
        verificationCode={verificationCode}
        onVerificationCodeChange={handleVerificationCodeChange}
        onVerificationCodeBlur={handleVerificationCodeBlur}
        onSubmit={handleSubmit}
        onResend={handleResendCode}
        isLoading={isLoading}
        inputError={!!inputError}
        inputHint={inputError}
        isCountdownRunning={countDown.isRunning}
        secondsLeft={countDown.secondsLeft}
        submitButtonLabel={submitCTA || getContentByPath('mfa.email.submit')}
        codeResentSuccessfully={codeResentSuccessfully}
        errorMessage={errorMessage}
        openAlert={openAlert}
        type={type}
      />
    </div>
  );
}

EmailVerificationSignin.displayName = 'EmailVerificationSignin';
